import styled from '@emotion/styled'
import { addMonths, addYears, getTime } from 'date-fns'
import { FC, useEffect, useState } from 'react'

import {
  Alert,
  Chart,
  DurationButtons,
  LoadingIndicator,
  Space,
  Typography
} from '@/components/core-v2'
import { TODAY } from '@/const'

export type ProjectionChartProps = {
  isError: boolean
  isLoading: boolean
  metrics: Record<string, number>[]
  title: string
}

const ranges = {
  '1m': (date: Date) => addMonths(date, 1),
  '3m': (date: Date) => addMonths(date, 3),
  '6m': (date: Date) => addMonths(date, 6),
  '1y': (date: Date) => addYears(date, 1),
  all: (date: Date) => addYears(date, 5)
}

function filterMetrics(metrics: Record<string, number>, range) {
  const endDateFn = ranges[range]
  const endDate = endDateFn(TODAY)

  const endTimestamp = getTime(endDate)

  // this needs to be improved
  const output = {}
  for (const date of Object.keys(metrics)) {
    if (Date.parse(date) <= endTimestamp) {
      output[date] = metrics[date]
    }
  }

  return output
}

function filterAllData(metrics: Record<string, number>[], range) {
  const output = metrics.map(m => filterMetrics(m, range))
  return output
}

const StyledChartTitle = styled.div`
  padding-bottom: 2px;
  border-bottom: 1px solid var(--White90);
`

const StyledProjectionChart = styled.div`
  min-height: 260px;
  border-bottom: solid 2px var(--White98);
`

export const ProjectionChart: FC<ProjectionChartProps> = ({
  isError,
  isLoading,
  metrics: rawMetrics,
  title
}) => {
  const [metrics, setMetrics] = useState([])
  const [selectedItem, setSelectedItem] = useState('all')

  useEffect(() => {
    if (rawMetrics) {
      setMetrics(rawMetrics)
    }
  }, [rawMetrics])

  if (isError) {
    return (
      <StyledProjectionChart>
        <Space px={5} py={6}>
          <StyledChartTitle>
            <Space mb={5}>
              <Typography fontSize="13px" letterSpacing="0" variant="b3">
                {title}
              </Typography>
            </Space>
          </StyledChartTitle>
          <Space mt={5}>
            <Alert title="Error loading projection" type="error">
              Contact us if you keep getting this error
            </Alert>
          </Space>
        </Space>
      </StyledProjectionChart>
    )
  }

  if (!metrics) {
    return (
      <StyledProjectionChart>
        <Space px={5} py={6}>
          <StyledChartTitle>
            <Space mb={5}>
              <Typography fontSize="13px" letterSpacing="0" variant="b3">
                {title}
              </Typography>
            </Space>
          </StyledChartTitle>
          <Space mt={5}>
            <LoadingIndicator isLoading={isLoading} />
          </Space>
        </Space>
      </StyledProjectionChart>
    )
  }

  return (
    <StyledProjectionChart>
      <Space px={5} py={6}>
        <StyledChartTitle>
          <Space mb={5}>
            <Typography fontSize="13px" letterSpacing="0" variant="b3">
              {title}
            </Typography>
          </Space>
        </StyledChartTitle>
        <Space mt={5}>
          <LoadingIndicator isLoading={isLoading}>
            <>
              <DurationButtons
                items={Object.keys(ranges)}
                selectedItem={selectedItem}
                onSelect={range => {
                  setMetrics(filterAllData(rawMetrics, range))
                  setSelectedItem(range)
                }}
              />
              <Chart metrics={metrics} />
            </>
          </LoadingIndicator>
        </Space>
      </Space>
    </StyledProjectionChart>
  )
}
